import {useMemo} from 'react'

import {buildResponsive} from '../../utils/gdi-responsive'
import {GDI_DESKTOP_MEDIA_QUERY, parseGDIViewTypes} from '../../utils/gdi-styles'

/* Spec defaults from GDI Remodel — applied when the author leaves a field at
 * "Default / Inherit" (or the field is absent entirely). Booleans here are
 * converted to Chakra `display` values downstream. */
const DEFAULTS = {
    ProductName: true,
    ProductPrice: true,
    WishlistIcon: false,
    OnImageBadge: false,
    ProductTags: true,
    QuickVIEWButton: true,
    QuickADDButton: false,
    ColorSwatches: false
}

/* CMS dropdown values: 'Yes' / 'No' / 'Default / Inherit' / null.
 * `buildResponsive` already filters 'Default / Inherit' and empty values, so
 * this getter only needs to resolve Yes/No → boolean. */
const yesNoGetter = (field) => (entry) => {
    const v = entry?.[field]
    if (v === 'Yes') return true
    if (v === 'No') return false
    return null
}

/* Convert a Chakra responsive boolean object (or scalar boolean) into a Chakra
 * responsive `display` value. `true` → `displayWhenOn`, `false` → 'none'. */
const toDisplay = (responsive, displayWhenOn) => {
    if (typeof responsive !== 'object' || responsive === null) {
        return responsive ? displayWhenOn : 'none'
    }
    const out = {}
    for (const [bp, v] of Object.entries(responsive)) {
        out[bp] = v ? displayWhenOn : 'none'
    }
    return out
}

const resolveQuickActionMode = (qvOn, qaOn) => {
    if (qvOn && !qaOn) return 'quick-view'
    if (!qvOn && qaOn) return 'quick-add'
    return null
}

const buildQuickActionMode = (qv, qa) => {
    const breakpoints = new Set([...Object.keys(qv || {}), ...Object.keys(qa || {})])
    if (breakpoints.size === 0) return resolveQuickActionMode(Boolean(qv), Boolean(qa))

    const out = {}
    for (const bp of breakpoints) {
        const qvOn = typeof qv === 'object' ? qv[bp] : qv
        const qaOn = typeof qa === 'object' ? qa[bp] : qa
        out[bp] = resolveQuickActionMode(Boolean(qvOn), Boolean(qaOn))
    }
    return out
}

/* WebDesktopHover is a hover STATE, not a breakpoint, so it lives outside the
 * regular WebMobile/Tablet/Desktop responsive resolution. Read it directly so
 * we can attach the override to a `:hover` selector instead of a breakpoint. */
const getHoverValue = (stylesArr, fieldGetter) => {
    if (!Array.isArray(stylesArr)) return null
    const hoverEntry = stylesArr.find(
        (e) => parseGDIViewTypes(e?.ViewTypesToApply).includes('_hover')
    )
    if (!hoverEntry) return null
    return fieldGetter(hoverEntry)
}

/* Build an sx fragment that combines a steady-state responsive `display` value
 * with an optional hover-state override. Hover override is wrapped in lg+
 * and `(hover: hover)` so it doesn't fire below desktop or on touch devices, where :hover is
 * unreliable. The override targets `[data-product-tile="true"]:hover &` so the
 * hover state is per-tile (not whole-carousel). */
const buildSx = (steady, hover, displayWhenOn = 'block') => {
    const sx = {display: toDisplay(steady, displayWhenOn)}
    if (hover !== null) {
        sx[`${GDI_DESKTOP_MEDIA_QUERY} and (hover: hover)`] = {
            '[data-product-tile="true"]:hover &': {
                display: hover ? displayWhenOn : 'none'
            }
        }
    }
    return sx
}

/* Build a Chakra responsive boolean for QV-OR-QA: visible when either toggle
 * is Yes for that breakpoint. Used as the gate for the QuickAdd overlay. */
const buildQuickActionsVisible = (qv, qa) => {
    const breakpoints = new Set([...Object.keys(qv || {}), ...Object.keys(qa || {})])
    if (breakpoints.size === 0) return Boolean(qv || qa)
    const out = {}
    for (const bp of breakpoints) {
        const qvOn = typeof qv === 'object' ? qv[bp] : qv
        const qaOn = typeof qa === 'object' ? qa[bp] : qa
        out[bp] = Boolean(qvOn || qaOn)
    }
    return out
}

/**
 * Resolve `ProductCarouselGridStylesPerViewType` into per-toggle sx fragments
 * consumable by GDIProductTileBlock → ProductTile. Each fragment is an
 * Emotion-style sx object combining a responsive steady-state `display`
 * value with an optional `WebDesktopHover` override applied via a `:hover`
 * selector on the tile root.
 */
export const useProductTileToggles = (stylesArr) => {
    return useMemo(() => {
        const get = (field) => {
            const fallback = DEFAULTS[field]
            const steady = buildResponsive(stylesArr, yesNoGetter(field), fallback)
            const hover = getHoverValue(stylesArr, yesNoGetter(field))
            return {steady, hover}
        }

        const productName = get('ProductName')
        const productPrice = get('ProductPrice')
        const wishlistIcon = get('WishlistIcon')
        const onImageBadge = get('OnImageBadge')
        const productTags = get('ProductTags')
        const colorSwatches = get('ColorSwatches')
        const quickView = get('QuickVIEWButton')
        const quickAdd = get('QuickADDButton')

        const quickActionsSteady = buildQuickActionsVisible(quickView.steady, quickAdd.steady)
        const forceTileMode = buildQuickActionMode(quickView.steady, quickAdd.steady)
        /* Hover state for QV/QA collapses to "either Yes" — we only need the
         * combined visibility for the overlay; mode-switching on hover is out
         * of scope (mode is decided once via forceTileMode). */
        const quickActionsHover =
            quickView.hover === null && quickAdd.hover === null
                ? null
                : Boolean(quickView.hover || quickAdd.hover)

        /* When every text/affordance toggle is Off at every breakpoint, the
         * tile renders only the image. Strip the details-wrapper min-height
         * (theme adds 88px on lg) and the image-wrapper margin-bottom so the
         * image fills the slide cleanly with no empty band underneath.
         * QuickVIEW/QuickADD are intentionally excluded — those render as
         * overlays on the image, not below it. */
        const isAnyOn = (responsive) => {
            if (typeof responsive !== 'object' || responsive === null) {
                return Boolean(responsive)
            }
            return Object.values(responsive).some(Boolean)
        }
        const imageOnlySteady =
            !isAnyOn(productName.steady) &&
            !isAnyOn(productPrice.steady) &&
            !isAnyOn(wishlistIcon.steady) &&
            !isAnyOn(productTags.steady) &&
            !isAnyOn(colorSwatches.steady)

        /* If the author configured a WebDesktopHover entry, fields that are
         * Off in the steady state may turn On during hover. Stripping the
         * details-wrapper min-height in that case causes a layout bounce when
         * the revealed text expands the tile. Detect a hover override on any
         * of the affected fields and keep the theme min-height (88px on lg)
         * to reserve space; the image-wrapper margin can still collapse. */
        const hasHoverOverride =
            productName.hover !== null ||
            productPrice.hover !== null ||
            wishlistIcon.hover !== null ||
            productTags.hover !== null ||
            colorSwatches.hover !== null
        const stripDetailsMinHeight = imageOnlySteady && !hasHoverOverride

        return {
            productName: buildSx(productName.steady, productName.hover),
            productPrice: buildSx(productPrice.steady, productPrice.hover),
            wishlistIcon: buildSx(wishlistIcon.steady, wishlistIcon.hover),
            onImageBadge: buildSx(onImageBadge.steady, onImageBadge.hover),
            productTags: buildSx(productTags.steady, productTags.hover),
            colorSwatches: buildSx(colorSwatches.steady, colorSwatches.hover),
            quickActionsDisplay: buildSx(quickActionsSteady, quickActionsHover, 'flex'),
            forceTileMode,
            /* Style overrides applied via Chakra prop spread AFTER the theme
             * styles so they win over `marginBottom: 2` / `minHeight: '88px'`. */
            imageWrapperOverride: imageOnlySteady ? {marginBottom: 0} : null,
            detailsWrapperOverride: stripDetailsMinHeight ? {minHeight: 'unset'} : null
        }
    }, [stylesArr])
}
